import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

/* =========================
 * Types
 * ========================= */

export type ModuleTableFilters = {
  name?: string;
};

export type ModuleRow = {
  id: string | number;
  name: string;
  parentName?: string;
};

/* =========================
 * Component
 * ========================= */
export default function ModulesTable({
  rows: _rows, // unused (kept for parity)
  pageRows,
  serialStart,
  filters,
  setFilters,
  onEdit,
  onDelete,
}: {
  rows: ModuleRow[];
  pageRows: ModuleRow[];
  serialStart: number;
  filters: ModuleTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<ModuleTableFilters>>;
  onEdit: (r: ModuleRow) => void;
  onDelete: (r: ModuleRow) => void;
}) {
  return (
    // Square bottom: the pagination bar below completes this card.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto overflow-visible">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Name</span>
                  <ColumnFilter label="Name">
                    <Input
                      placeholder="Contains…"
                      value={filters.name || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th>Parent module</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                {/* 4 columns total */}
                <td
                  colSpan={4}
                  className="px-5 py-10 text-center text-sm text-gray-500"
                >
                  No modules found.
                </td>
              </tr>
            ) : (
              pageRows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <Td className="text-gray-700">{serialStart + i + 1}</Td>

                  <Td className="text-gray-900">{r.name}</Td>

                  <Td className="text-gray-700">
                    {r.parentName ? (
                      <span className="whitespace-nowrap">{r.parentName}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Td>

                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onEdit(r)}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(r)}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="md:hidden divide-y divide-gray-200">
        {pageRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            No modules found.
          </div>
        ) : (
          pageRows.map((r, i) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {serialStart + i + 1}. {r.name}
                  </div>
                  {r.parentName && (
                    <div className="text-xs text-gray-500">
                      Parent: {r.parentName}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onEdit(r)}
                    aria-label="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(r)}
                    aria-label="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* =========================
 * Small helpers
 * ========================= */
function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}
